<script setup lang="ts">
import 'vidstack/player'
import 'vidstack/player/layouts'
import 'vidstack/player/ui'
import 'vidstack/player/styles/default/theme.css'
import 'vidstack/player/styles/default/layouts/audio.css'

import { onMounted, ref, useTemplateRef } from 'vue'
import { translations } from '@scripts/i18n.js'
import { MediaPlayerElement } from 'vidstack/elements'

const props = defineProps<{
    title: string
    src: string
    assetId: string
    mimeType: string
    poster?: string
    chapters?: string
}>()

const containerRef = useTemplateRef('container')
const player = ref<MediaPlayerElement>()

onMounted(() => {
    const outlet: HTMLElement | null | undefined =
        containerRef.value?.closest('#outlet')
    if (!outlet) return
    outlet.style.setProperty('--aspect-ratio', 'auto')
    outlet.style.setProperty('--height', '160px')
})

const playMedia = () => {
    const floatingPlayerKeys = Object.keys(window.floatingPlayers)

    floatingPlayerKeys.forEach((key) => {
        if (key !== props.assetId) {
            window.floatingPlayers[key]?.pauseMediaPlayer()
        }
    })
}
</script>

<template>
    <div
        class="bg-muted flex flex-col flex-wrap rounded-md lg:flex-row lg:flex-nowrap"
        ref="container"
    >
        <div
            v-if="poster"
            class="sm:floating:hidden relative w-full flex-none overflow-hidden rounded-t-md lg:mt-0 lg:size-40 lg:rounded-l-md"
        >
            <img
                :src="poster"
                class="size-full object-cover object-center"
                :alt="title"
            />
        </div>
        <media-player
            class="player flex-1 overflow-visible py-6 lg:!w-[480px] lg:py-0"
            :title="title"
            crossorigin
            playsinline
            ref="player"
            load="eager"
            keep-alive
            @play="playMedia()"
        >
            <media-provider>
                <source
                    :src="src"
                    :type="mimeType"
                />
                <track
                    v-if="chapters"
                    :src="chapters"
                    kind="chapters"
                    default
                />
            </media-provider>
            <media-audio-layout
                :translations="translations"
            ></media-audio-layout>
        </media-player>
    </div>
</template>

<style>
.vds-audio-layout {
    --audio-brand: var(--color-brand);
    --audio-controls-color: var(--color-black);
    --audio-focus-ring-color: rgb(78 156 246);
    --audio-focus-ring: 0 0 0 3px var(--media-focus-ring-color);
    --audio-font-family: var(--font-sans);

    --audio-bg: var(--color-muted);
    --audio-border-radius: 0 6px 6px 0;
    --audio-border: none;
    --audio-filter: none;

    /* Buttons. */
    --audio-button-size: 40px;

    --audio-play-button-size: 44px;
    --audio-play-button-color: var(--color-brand-foreground);
    --audio-play-button-bg: var(--color-brand);
    --audio-play-button-border-radius: 100%;
    --audio-caption-button-off-opacity: 0.64;

    /* Sliders. */
    --audio-slider-chapter-title-color: black;
    --audio-slider-value-border: 1px solid rgb(255 255 255 / 0.1);

    --audio-volume-gap: 18px;
    --audio-volume-height: 96px;
    --audio-volume-bg: var(--color-white);
    --audio-volume-border-radius: 8px;

    /* Menus. */
    --audio-menu-max-height: 320px;

    /* Buffering. */
    --audio-buffering-stripe-color: rgb(0 0 0 / 0.25);
    --audio-buffering-stripe-size: 30px;
    --audio-buffering-stripe-speed: 2s;

    /* Captions. */
    --audio-captions-offset: 4px;
    --audio-captions-bg: transparent;
    --audio-cue-font-size: 14px;
    --audio-cue-color: white;
    --audio-cue-border: 1px solid rgb(255 255 255 / 0.1);
    --audio-cue-bg: black;

    /* Chapter Title. */
    --media-chapter-title-color: var(--color-stone-500);
    --media-chapter-title-font-size: inherit;
    --media-chapter-title-font-weight: 500;
    --media-chapter-title-separator-color: var(--color-stone-500);
    --media-chapter-title-separator-gap: 6px;
    --media-chapter-title-separator: '\2022';

    /* Time */
    --audio-time-font-size: 15px;

    height: 100%;

    .vds-controls {
        padding-inline: 2rem;
    }

    .vds-time {
        color: black !important;
    }

    .vds-play-button {
        max-width: unset;
    }

    .vds-title {
        font-weight: 600;
    }
}

#keepalive .vds-audio-layout {
    height: 96px;

    .vds-settings-menu,
    .vds-title {
        display: none !important;
    }

    .vds-controls {
        padding-inline: 1rem;
    }
}
</style>
